import type { ReactNode } from 'react';
import { cx } from '../cx';
import { TICK_TEXT } from './scale';
import { NARROW_ONLY, WIDE_ONLY, type ChartLayout } from './layout';

export type PercentileStripProps = {
  /** Where the reader stands, 0 to 100 ("your pay is higher than 64% of ads" is 64). */
  percentile: number;
  /** The marker's words ("You: higher than 64% of ads"). */
  label: ReactNode;
  /** The band's three markers, translated ("Lowest quarter", "Median", "Top quarter"). */
  quartileLabels: [ReactNode, ReactNode, ReactNode];
  /** One sentence for screen readers. */
  description: string;
  /** `auto` (default) switches to the narrow form in a narrow container: the reader's words sit above the strip in full width and only the two end quarters are named. */
  layout?: ChartLayout;
  className?: string;
};

/**
 * Where the reader stands among everyone else: a band in four quarters with the median marked,
 * and the reader as an ink marker with words. For "your pay against the market" and "your
 * profile against other applicants". Always about the reader, never a ranking of others.
 */
const PAD: Record<ChartLayout, string> = { auto: '@md:pt-7', narrow: '', wide: 'pt-7' };
const TICKS: Record<ChartLayout, string> = {
  auto: 'flex justify-between @md:grid @md:grid-cols-3',
  narrow: 'flex justify-between',
  wide: 'grid grid-cols-3',
};

export function PercentileStrip({
  percentile,
  label,
  quartileLabels,
  layout = 'auto',
  description,
  className,
}: PercentileStripProps) {
  const p = Math.max(0, Math.min(100, percentile));
  return (
    <div role="img" aria-label={description} className={cx('@container', className)}>
      <div className={cx('flex flex-col gap-1', PAD[layout])}>
        <span className={cx('text-xs font-semibold text-jc-ink', NARROW_ONLY[layout])} aria-hidden="true">
          {label}
        </span>
        <div className="relative">
          <div className="flex h-4 gap-0.5 overflow-hidden rounded-jc-info" aria-hidden="true">
            {['bg-jc-chart-seq-1', 'bg-jc-chart-seq-2', 'bg-jc-chart-seq-3', 'bg-jc-chart-seq-4'].map((c) => (
              <span key={c} className={cx('flex-1', c)} />
            ))}
          </div>
          <span className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-jc-ink-secondary" aria-hidden="true" />
          <span
            className="absolute -top-1 h-6 w-1 -translate-x-1/2 rounded-full bg-jc-ink ring-2 ring-jc-surface"
            style={{ left: `${p}%` }}
          >
            <span
              className={cx(
                'absolute bottom-full mb-1 text-xs font-semibold whitespace-nowrap text-jc-ink',
                WIDE_ONLY[layout],
                p > 70 ? 'right-0' : p < 30 ? 'left-0' : 'left-1/2 -translate-x-1/2',
              )}
            >
              {label}
            </span>
          </span>
        </div>
        <div className={cx(TICKS[layout], TICK_TEXT)} aria-hidden="true">
          <span>{quartileLabels[0]}</span>
          <span className={cx('text-center', WIDE_ONLY[layout])}>{quartileLabels[1]}</span>
          <span className="text-right">{quartileLabels[2]}</span>
        </div>
      </div>
    </div>
  );
}
